import { NoticeDialogLink } from "@/components/privacy/Notice";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Skel } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";
import { deviceInfo } from "@/lib/assessments";
import { AssessFrame, AssessDone } from "@/pages/assess/AssessPage";
import { cn } from "@/lib/utils";
import { PageTour } from "@/components/app/PageTour";
import { LiveInterview } from "@/pages/interview/LiveInterview";
import { RECORD_NOTICE } from "@/pages/interview/DeviceCheck";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function Chat({ token, onDone }) {
  const qc = useQueryClient();
  const { data: st, refetch } = useQuery({ queryKey: ["iv-state", token], queryFn: () => get(`/public/interview/${token}/state`),
    refetchInterval: (q) => (q.state.data?.pending ? 1500 : false), refetchOnWindowFocus: false });
  const [text, setText] = useState("");
  const [left, setLeft] = useState(null);
  const [busy, setBusy] = useState(false);
  const shown = useRef(Date.now());
  const paste = useRef(0);
  const tabs = useRef(0);
  const key = st ? `${st.question_number}-${st.followup}-${st.pending}` : "";
  const endRef = useRef(null);

  useEffect(() => { if (st?.status === "completed") onDone(); }, [st, onDone]);
  useEffect(() => { if (st && !st.pending) { setText(st.draft || ""); setLeft(st.remaining_seconds); shown.current = Date.now(); paste.current = 0; } endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const v = () => { if (document.visibilityState === "hidden") { tabs.current += 1; api.post(`/public/interview/${token}/draft`, { text: textRef.current }).catch(() => {}); } }; document.addEventListener("visibilitychange", v); return () => document.removeEventListener("visibilitychange", v); }, [token]);
  useEffect(() => { if (!st || st.pending) return undefined; const t = setInterval(() => api.post(`/public/interview/${token}/draft`, { text: textRef.current }).catch(() => {}), 10000); return () => clearInterval(t); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const textRef = useRef("");
  textRef.current = text;

  const send = useCallback(async (auto) => {
    if (busy) return;
    if (!auto && !textRef.current.trim()) { toast.error("Please write an answer first."); return; }
    setBusy(true);
    try {
      await api.post(`/public/interview/${token}/answer`, { text: textRef.current, ms: Date.now() - shown.current, paste_chars: paste.current, tab_switches: tabs.current });
      tabs.current = 0; setText("");
      if (auto) toast.info("Time's up for this question — your answer was sent.");
      qc.setQueryData(["iv-state", token], (d) => ({ ...d, pending: true })); refetch();
    } catch (e) { toast.error(errMsg(e)); refetch(); }
    setBusy(false);
  }, [busy, token, qc, refetch]);

  useEffect(() => {
    if (left == null || st?.pending) return undefined;
    if (left <= 0) { send(true); return undefined; }
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left, st, send]);

  if (!st || st.status === "completed") return <Skel className="h-80" />;
  return (
    <div className="space-y-4" data-testid="iv-chat">
      {!st.pending && <div className="space-y-1"><div className="flex justify-between text-xs text-ink-muted"><span data-testid="iv-progress-label">Question {st.question_number} of {st.total}{st.followup ? " · follow-up" : ""}</span><span>Saves automatically</span></div><Progress value={(100 * (st.question_number - 1)) / st.total} className="h-2 bg-brand-tint" /></div>}
      <div className="space-y-3" data-testid="iv-history" aria-live="polite" aria-label="Interview conversation">
        {st.history.map((h, i) => (
          <div key={i} className="space-y-2">
            <div className="flex gap-2"><Bot size={18} className="mt-1 shrink-0 text-brand" /><p className="rounded-2xl rounded-tl-sm bg-white px-4 py-2.5 text-sm shadow-sm">{h.prompt}</p></div>
            <p className="ml-auto max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-tr-sm bg-brand px-4 py-2.5 text-sm text-white">{h.answer || <em className="opacity-70">No answer</em>}</p>
          </div>
        ))}
      </div>
      {st.pending ? <p className="flex items-center gap-2 text-sm text-ink-muted" data-testid="iv-pending"><Loader2 size={14} className="animate-spin" /> Reading your answer…</p> : (
        <div className="space-y-3">
          <div className="flex gap-2"><Bot size={18} className="mt-1 shrink-0 text-brand" /><p className="rounded-2xl rounded-tl-sm bg-white px-4 py-3 text-base font-medium shadow-sm" data-testid="iv-prompt">{st.prompt}</p></div>
          <div className="panel space-y-2 p-3">
            <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} onPaste={(e) => { paste.current += (e.clipboardData.getData("text") || "").length; }} placeholder="Type your answer…" className="border-0 text-base focus-visible:ring-0" data-testid="iv-answer-input" />
            <div className="flex items-center justify-between">
              <span className={cn("font-display text-lg font-semibold tabular-nums", left <= 30 && "text-risk")} data-testid="iv-timer">{fmt(Math.max(0, left ?? 0))}</span>
              <Button onClick={() => send(false)} disabled={busy} className="h-11 px-6" data-testid="iv-send-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send</Button>
            </div>
          </div>
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}

export default function InterviewPage() {
  const { token } = useParams();
  const [step, setStep] = useState(null);
  const [agree, setAgree] = useState(false);
  const { data: info, isLoading, error, refetch } = useQuery({ queryKey: ["iv-public", token], queryFn: () => get(`/public/interview/${token}`), retry: false, staleTime: Infinity });
  const done = useCallback(() => setStep("done"), []);
  if (isLoading) return <AssessFrame><Skel className="h-72" /></AssessFrame>;
  if (error) return <AssessFrame><div className="panel space-y-2 p-6" data-testid={error.response?.status === 410 ? "iv-expired" : "iv-invalid"}><h1 className="text-2xl font-semibold">{error.response?.status === 410 ? "This link has expired" : "This link isn't valid"}</h1><p className="text-ink-secondary">{errMsg(error)}</p></div></AssessFrame>;
  if (info.status === "completed" || step === "done") return <AssessFrame info={info}><div data-testid="iv-done"><AssessDone info={info} /></div></AssessFrame>;
  const start = async () => { try { await api.post(`/public/interview/${token}/consent`, { version: info.consent_version, device: deviceInfo() }); await refetch(); setStep("chat"); } catch (e) { toast.error(errMsg(e)); } };
  const verbal = info.mode === "verbal";
  return (
    <AssessFrame info={info}>
      {info.consented || step === "chat" ? (verbal ? <LiveInterview token={token} onDone={done} /> : <Chat token={token} onDone={done} />) : (
        <article className="panel space-y-5 p-5 sm:p-8" data-tour="pt-iv-intro" data-testid="iv-intro">
      <div className="flex justify-end"><PageTour /></div>
          <p className="text-sm text-ink-muted">{info.job_title}</p>
          <h1 className="text-3xl font-semibold tracking-tight">{verbal ? "Your live voice interview" : "Your written interview"}</h1>
          {verbal ? (
            <ul className="space-y-1.5 text-ink-secondary" data-testid="iv-verbal-steps">
              <li>A live voice call with an AI interviewer (audio only). About 5 minutes, 10 minutes at most.</li>
              <li>It asks questions from the HR and Legal rounds, with natural follow-ups. Speak normally; you can interrupt it or ask it to repeat or rephrase.</li>
              <li>Use headphones and a quiet room if you can. You'll need a working microphone.</li>
              <li>If your connection drops, you can rejoin and continue where you left off.</li>
              <li className="font-medium text-ink" data-testid="iv-intro-record-notice">{RECORD_NOTICE}: your camera and both voices. Camera is recommended; voice only is allowed.</li>
            </ul>
          ) : <p className="text-ink-secondary">{info.questions} questions, one at a time, with up to {info.answer_minutes} minutes per answer. You can close the page and come back — you'll continue at the same question.</p>}
          <p className="rounded-[6px] bg-brand-tint/60 p-4 text-sm leading-relaxed text-ink-secondary" data-tour="pt-iv-disclosure" data-testid="iv-disclosure">{info.disclosure}</p>
          <label className="flex items-start gap-3 rounded-[6px] border border-line bg-subtle p-4 text-sm text-ink-secondary"><Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-tour="pt-iv-consent-checkbox" data-testid="iv-consent-checkbox" /><span>{info.consent_text} <NoticeDialogLink testId="iv-notice-link" /></span></label>
          <Button className="h-12 w-full text-base sm:w-auto sm:px-8" disabled={!agree} onClick={start} data-testid="iv-start-btn">Start the interview</Button>
        </article>
      )}
    </AssessFrame>
  );
}
